import { useQuery } from "@tanstack/react-query";
import { Users, PartyPopper, Utensils, UserX, Send } from "lucide-react";
import { toast } from "sonner";
import { api, friendlyError } from "@/lib/api";
import { CountUp } from "@/components/CountUp";
import { STATUS_LABELS } from "@/lib/constants";
import { useState } from "react";

function Stat({ icon: Icon, value, label, testid, tone = "gold" }) {
  const tones = { gold: "bg-goldLight text-gold", green: "bg-[#2D6A4F]/10 text-[#2D6A4F]", orange: "bg-[#D97706]/10 text-[#D97706]", ink: "bg-espresso/10 text-espresso" };
  return (
    <div className="rounded-3xl bg-white border border-hair shadow-soft p-6" data-testid={testid}>
      <div className={`w-11 h-11 rounded-full grid place-items-center mb-4 ${tones[tone]}`}><Icon size={20} /></div>
      <div className="font-serif text-4xl text-espresso"><CountUp value={value} /></div>
      <p className="text-eyebrow text-inkMuted mt-1">{label}</p>
    </div>
  );
}

export function Overview({ onManageGuests }) {
  const { data: stats } = useQuery({ queryKey: ["host-stats"], queryFn: async () => (await api.get("/host/stats")).data });
  const { data: rsvps = [] } = useQuery({ queryKey: ["host-rsvps"], queryFn: async () => (await api.get("/host/rsvps")).data });
  const [sending, setSending] = useState(false);

  const sendReminder = async () => {
    setSending(true);
    try {
      const res = await api.post("/host/send-reminder");
      toast.success(`Reminder texted to ${res.data.sent} ${res.data.sent === 1 ? "guest" : "guests"}. 🥂`);
    } catch (e) { toast.error(friendlyError(e)); } finally { setSending(false); }
  };

  const recent = rsvps
    .flatMap((r) => (r.history || []).map((h) => ({ ...h, name: r.primary_name })))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 8);

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="font-serif text-3xl text-espresso">Overview</h1>
        <p className="text-inkSoft mt-1">Everything at a glance.</p>
      </div>
      <div className="rounded-3xl bg-espressoDeep text-cream p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="reminder-card">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center text-goldLight shrink-0"><Send size={20} /></div>
          <div>
            <p className="font-serif text-xl">Guest Reminders</p>
            <p className="text-white/60 text-sm mt-0.5">Auto-texts everyone the day before. Want to nudge them now?</p>
          </div>
        </div>
        <button onClick={sendReminder} disabled={sending} className="gold-gradient text-espressoDeep font-bold px-6 py-3 rounded-full shadow-gold active:scale-95 transition disabled:opacity-50 whitespace-nowrap" data-testid="send-reminder-now">
          {sending ? "Sending…" : "Send Reminder Now"}
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} value={stats?.total_guests || 0} label="Guests Attending" testid="stat-attending" tone="green" />
        <Stat icon={PartyPopper} value={stats?.parties || 0} label="RSVP Parties" testid="stat-parties" tone="gold" />
        <Stat icon={Utensils} value={stats?.dishes || 0} label="Dishes" testid="stat-dishes" tone="ink" />
        <Stat icon={UserX} value={stats?.declined || 0} label="Declined" testid="stat-declined" tone="orange" />
      </div>

      {stats?.max_capacity && (
        <div className="rounded-3xl bg-white border border-hair shadow-soft p-6" data-testid="capacity-card">
          <div className="flex justify-between items-end mb-3">
            <div><p className="text-eyebrow text-inkMuted">Capacity</p><p className="font-serif text-2xl text-espresso mt-1">{stats.total_guests} / {stats.max_capacity} Confirmed</p></div>
            <p className="text-sm font-semibold text-gold">{stats.spots_remaining} spots left</p>
          </div>
          <div className="h-3 rounded-full bg-cream2 overflow-hidden"><div className="h-full gold-gradient" style={{ width: `${Math.min(100, (stats.total_guests / stats.max_capacity) * 100)}%` }} /></div>
        </div>
      )}

      <div className="rounded-3xl bg-white border border-hair shadow-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-espresso">Recent Changes</h2>
          <button onClick={onManageGuests} className="text-sm font-semibold text-gold hover:underline" data-testid="overview-manage-guests">Manage guests →</button>
        </div>
        {recent.length === 0 ? <p className="text-inkSoft text-sm">No activity yet.</p> : (
          <div className="divide-y divide-hair" data-testid="recent-changes">
            {recent.map((h, i) => (
              <div key={i} className="py-3 flex items-center justify-between gap-4">
                <div><p className="text-espresso font-medium">{h.name}</p><p className="text-sm text-inkSoft">{h.change_type}{h.new ? `: ${h.new}` : ""}</p></div>
                <span className="text-xs text-inkMuted whitespace-nowrap">{new Date(h.timestamp).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
